import { isSuggestedTag } from "@/lib/tagScope";
import { useState, useMemo } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { sortTags } from "@/lib/utils/tagSorting";
import { Pill } from "@/components/ui/Pill";
import { TagPill } from "@/components/TagPill";
import type { Tag } from "@/services/tags.service";
import { CAPTION, LABEL } from "@/lib/typography";

const SKELETON_WIDTHS = [56, 76, 56, 96, 76];
const TAG_RAIL_CLASS_NAME = [
  "flex min-h-8 gap-2 overflow-x-auto",
  "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  "-mx-gutter px-gutter",
].join(" ");

const getTagRailClassName = (expanded: boolean, className?: string) =>
  cn(TAG_RAIL_CLASS_NAME, expanded && "md:flex-wrap md:overflow-visible", className);

type TagShowLevel = 0 | 1 | 2;
interface DisclosureControl {
  label: string;
  nextLevel: TagShowLevel;
}

const getDisclosureControl = ({
  showLevel,
  tier1Count,
  tier2Count,
  totalTags,
}: {
  showLevel: TagShowLevel;
  tier1Count: number;
  tier2Count: number;
  totalTags: number;
}): DisclosureControl | null => {
  if (showLevel === 0 && tier2Count > tier1Count) {
    return { label: `show ${tier2Count - tier1Count} more`, nextLevel: 1 };
  }
  if (showLevel === 1 && totalTags > tier2Count) {
    return { label: `show ${totalTags - tier2Count} more`, nextLevel: 2 };
  }
  if (showLevel > 0) {
    return { label: "show less", nextLevel: 0 };
  }
  return null;
};

interface TagWithCount extends Tag {
  count: number;
}

export type TagLogicMode = "any" | "all";
export type TagDisplayStyle = "default" | "colored";

interface TagsWithShowHideProps {
  tags: TagWithCount[];
  selectedTag?: string | null | string[];
  onTagClick: (tagId: string) => void;
  logicMode?: TagLogicMode;
  onLogicModeChange?: (mode: TagLogicMode) => void;
  className?: string;
  title?: string;
  tagDisplayStyle?: TagDisplayStyle;
}

export function TagsWithShowHide({
  tags,
  selectedTag,
  onTagClick,
  logicMode = "any",
  onLogicModeChange,
  className,
  title,
  tagDisplayStyle = "default",
}: TagsWithShowHideProps) {
  const [tagShowLevel, setTagShowLevel] = useState<TagShowLevel>(0);

  // Sort tags by most recently used
  const sortedTags = useMemo(() => sortTags(tags, "recent"), [tags]);
  const actualTags = useMemo(() => sortedTags.filter(tag => tag.id !== "all"), [sortedTags]);
  const totalTags = actualTags.length;
  const minShowTags = Math.min(12, totalTags);
  const tier2Size = totalTags > minShowTags ? Math.max(8, Math.floor(Math.log2(totalTags) * 4)) : 0;
  const tier1Count = minShowTags;
  const tier2Count = Math.min(tier1Count + tier2Size, totalTags);
  const visibleCount = tagShowLevel === 0 ? tier1Count : tagShowLevel === 1 ? tier2Count : totalTags;
  const visibleTags = actualTags.slice(0, visibleCount);

  if (actualTags.length === 0) {
    return null;
  }

  // Normalize selectedTag to array
  const selectedTagsArray = Array.isArray(selectedTag)
    ? selectedTag
    : selectedTag && selectedTag !== "all"
      ? [selectedTag]
      : [];

  const hasSelection = selectedTagsArray.length > 0;
  const hasMultipleSelection = selectedTagsArray.length > 1;
  const isColoredDisplay = tagDisplayStyle === "colored";
  const disclosureControl = getDisclosureControl({ showLevel: tagShowLevel, tier1Count, tier2Count, totalTags });
  const renderDisclosureControl = () => {
    if (!disclosureControl) return null;

    return (
      <Pill
        size={isColoredDisplay ? "sm" : "md"}
        onClick={() => setTagShowLevel(disclosureControl.nextLevel)}
      >
        {disclosureControl.label}
      </Pill>
    );
  };

  return (
    <div className={cn("tag-filter", title && "space-y-2", className)}>
      {title && (
        <div className="flex items-center gap-2 h-5">
          <h3 className={cn(LABEL, "text-muted-foreground")}>{title}</h3>
        </div>
      )}
      <div className={getTagRailClassName(tagShowLevel > 0)}>
        {/* Inline controls: clear button and any/and toggle */}
        {hasSelection && (
          <Pill
            onClick={() => onTagClick("all")}
            size={isColoredDisplay ? "sm" : "md"}
            ariaLabel="Clear tag filter"
          >
            <X className="size-3.5 shrink-0" aria-hidden="true" />
            Clear
          </Pill>
        )}
        {hasMultipleSelection && onLogicModeChange && (
          <div className="flex-shrink-0 flex items-center gap-1 rounded-full bg-muted p-0.5">
            <button
              onClick={() => onLogicModeChange("any")}
              className={cn(
                LABEL,
                "rounded-full px-2 py-0.5",
                logicMode === "any"
                  ? "bg-background text-foreground"
                  : "text-muted-foreground",
              )}
              aria-label="Show posts with any selected tag"
              aria-pressed={logicMode === "any"}
            >
              Any
            </button>
            <span className={CAPTION}>/</span>
            <button
              onClick={() => onLogicModeChange("all")}
              className={cn(
                LABEL,
                "rounded-full px-2 py-0.5",
                logicMode === "all"
                  ? "bg-background text-foreground"
                  : "text-muted-foreground",
              )}
              aria-label="Show posts with all selected tags"
              aria-pressed={logicMode === "all"}
            >
              All
            </button>
          </div>
        )}

        {visibleTags.map(tag => {
          const isSelected = selectedTagsArray.includes(tag.id);
          if (isColoredDisplay) {
            return (
              <TagPill
                key={tag.id}
                name={tag.name}
                color={tag.color}
                selected={isSelected}
                suggested={isSuggestedTag(tag)}
                count={tag.count}
                onClick={() => onTagClick(tag.id)}
              />
            );
          }

          return (
            <Pill
              key={tag.id}
              variant={isSelected ? "active" : "muted"}
              size="md"
              onClick={() => onTagClick(tag.id)}
              className="flex-shrink-0 border"
            >
              {isSelected && <X className="h-3 w-3 mr-1" />}
              {tag.name.toLowerCase()} ({tag.count})
            </Pill>
          );
        })}

        {renderDisclosureControl()}
      </div>
    </div>
  );
}
